import React from 'react';
import { XCircle, RefreshCw, ArrowLeft } from 'lucide-react';

interface PaymentErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactionId?: string;
  onRetry?: () => void;
  onGoBack?: () => void;
}

const PaymentErrorModal: React.FC<PaymentErrorModalProps> = ({
  isOpen,
  onClose,
  transactionId,
  onRetry,
  onGoBack
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center animate-fadeIn overflow-hidden">
      {/* Overlay para cerrar al hacer clic fuera */}
      <div className="absolute inset-0" onClick={onClose}></div>
      
      {/* Contenedor modal */}
      <div className="relative z-10 w-full max-w-md mx-auto px-4">
        <div className="bg-gradient-to-br from-gray-900 via-red-900/30 to-gray-900 rounded-xl p-6 border border-red-500/30 shadow-xl">
          {/* Efectos de fondo */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-red-500/10 rounded-full blur-xl"></div>
          <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-orange-500/10 rounded-full blur-xl"></div>
          
          {/* Botón de cierre */}
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 text-white bg-gray-800/80 hover:bg-gray-700 p-1.5 rounded-full transition-colors"
            aria-label="Cerrar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Icono de error */}
          <div className="text-center mb-5">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20 mb-4">
              <XCircle className="h-10 w-10 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              Tu pago no ha sido procesado
            </h2>
            <p className="text-gray-300 text-sm">
              Detectamos que cancelaste el proceso de pago, pero el sistema intentó acreditar las monedas. Este problema ha sido registrado automáticamente.
            </p>
          </div>
          
          {/* Detalles de la transacción */}
          {transactionId && (
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-3 mb-5">
              <p className="text-xs text-gray-400 mb-1">ID de transacción:</p>
              <p className="text-sm text-gray-300 font-mono">{transactionId}</p>
            </div>
          )}
          
          {/* Botones de acción */}
          <div className="flex flex-col gap-3 mt-5">
            {onRetry && (
              <button
                onClick={onRetry}
                className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-medium rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Intentar pago nuevamente</span>
              </button>
            )}
            
            {onGoBack && (
              <button
                onClick={onGoBack}
                className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Volver a la página anterior</span>
              </button>
            )}
            
            <button
              onClick={onClose}
              className="w-full px-4 py-2.5 bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 font-medium rounded-lg transition-all duration-300"
            >
              Cerrar esta ventana
            </button>
          </div>
          
          <p className="text-center text-xs text-gray-500 mt-4">
            Si crees que esto es un error, contacta a nuestro soporte técnico.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentErrorModal; 